(ns graph-ui.web.theme
  "Rich theming for the web renderer (ADR-graph-ui-v0-009-01).

   A theme is a named map of design-token -> colour (generated-themes/theme-data,
   the committed mirror of dsl/instances/themes.edn). Applying a theme writes its
   tokens as CSS custom properties on the document root; the whole UI recolours
   because index.html maps Tailwind's palette onto those same variables. Nothing
   re-renders — a theme switch is a style write.

   Amber is the default (recreates Alatus / ikc1). The chosen theme is persisted
   to localStorage so it survives a reload. The full theme EDITOR (creating and
   saving new themes) lands with the ikc1 chrome; this namespace is the runtime
   apply + a minimal selector."
  (:require [reagent.core :as r]
            [clojure.string :as str]
            [cljs.reader :as reader]
            [graph-ui.web.generated-themes :as gen]
            [graph-ui.web.responsive :as responsive]
            ["@mantine/core" :refer [createTheme]]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @adr ADR-graph-ui-v0-009-01-rich-theming-as-data
;; @adr ADR-graph-ui-v0-012-01-mantine-component-library
;; @adr ADR-graph-ui-v0-020-01-a-widget-tree-can-name-its-surface

(def ^:private themes  (:themes gen/theme-data))
(def ^:private default (:default gen/theme-data))
(def ^:private storage-key "ikc3-theme")
(def ^:private overrides-key "ikc3-theme-overrides")

(defonce active (r/atom default))

(defonce mantine-scheme (r/atom "light"))

(defn- hex->rgb
  "Parse `#RRGGBB` -> [r g b] integers, or nil."
  [hex]
  (when-let [m (re-matches #"#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})" (str hex))]
    [(js/parseInt (m 1) 16) (js/parseInt (m 2) 16) (js/parseInt (m 3) 16)]))

(defn- rgb-brightness
  "Relative luminance (0..1) of an [r g b] triple."
  [[r g b]]
  (let [f #(let [x (/ % 255)] (if (<= x 0.03928) (/ x 12.92) (js/Math.pow (/ (+ x 0.055) 1.055) 2.4)))]
    (+ (* 0.2126 (f r)) (* 0.7152 (f g)) (* 0.0722 (f b)))))

(defn- theme-scheme
  "Derive a Mantine color scheme ('light' or 'dark') from the active theme's
   ground token — dark ground => dark scheme."
  [tokens]
  (if-let [ground (hex->rgb (:ground tokens))]
    (if (< (rgb-brightness ground) 0.5) "dark" "light")
    "light"))

;; Per-theme token edits made in the editor, `{theme-key {token value}}`. A theme
;; is base data ⊕ its overrides; keeping edits here (not just as CSS vars) is what
;; lets an edit survive switching to another theme and back. This is the interim
;; store until themes become bus-backed frames and edits save as named themes.
(defonce ^:private overrides (r/atom {}))

(defn theme-keys
  "Theme keys in a stable display order (default first, then the rest)."
  []
  (cons default (remove #(= default %) (keys themes))))

(def ^:private base
  "The non-colour token defaults — measure, type, space, radius, stroke,
   elevation (PLAN-graph-ui-v0-013-01 Phase 0). A type scale is not a palette: the
   five themes differ in colour and agree about rhythm, so these have defaults and
   a theme states only what it wants to differ on. Colour has no :base entry —
   there is no sensible default palette."
  (:base gen/theme-data))

(defn- effective-tokens
  "Theme `k`'s tokens: the shared base, then the theme's own, then editor
   overrides. Three layers, each able to restate the one below it, which is what
   keeps these tokens rather than constants — a denser or larger-type theme is a
   theme that restates `space` and `type`."
  [k]
  (merge base (get-in themes [k :tokens]) (get @overrides k)))

(def ^:private semantic-colours
  "The four token names Mantine's own palette is pointed at. `accent` is the
   primary; the other three are what a :tone prop resolves to."
  [:accent :good :warn :crit])

(defn- shades
  "One colour as Mantine's ten-shade array.

   THE SAME VALUE TEN TIMES, which is what this has always done. A real ramp
   per token is the follow-up ADR-graph-ui-v0-012-01 calls a full token to
   colour-scale mapping; repeating the value keeps every shade ON the token
   rather than near it."
  [hex]
  (into-array (repeat 10 (str hex))))

(defn mantine-theme
  "Mantine's own palette, BUILT FROM THE ACTIVE THEME (ADR-graph-ui-v0-012-01).

   IT WAS A `def` OF HEX LITERALS UNTIL 2026-09-11, and that is the whole
   defect. `createTheme` ran once at namespace load with Amber's green written
   into it, so the UI had TWO colour systems: the CSS custom properties, which
   follow the theme editor, and Mantine's palette, which never moved. Editing
   --accent recoloured half the screen and left every Button, Badge and Tab
   accent at Amber's green in every theme. George, 2026-09-11: the configurator
   \"shows colors that require a lot of experiments to find out where are
   applied\" — and half the experiments could not work.

   A FUNCTION, AND IT DEREFS `active`. Every call site is inside a reagent
   render that already derefs mantine-scheme, so the provider is rebuilt when a
   theme is chosen or a token is edited.

   THE FONT AND THE RADIUS COME FROM TOKENS TOO. They are declared
   (dsl/instances/themes.edn :base) and Mantine was using its own defaults, so
   a theme that restated either changed the CSS half of the UI and not the
   component half."
  []
  (let [t (effective-tokens @active)]
    (createTheme
      #js {:primaryColor "accent"
           :colors (clj->js (into {} (for [c semantic-colours]
                                       [c (shades (get t c))])))
           :defaultRadius (str (get t :radius-md "0.375rem"))
           :fontFamily (str (get t :font-ui))
           :fontFamilyMonospace (str (get t :font-mono))})))

(defn floor-chars
  "The character count in a `measure` token — \"45ch\" → 45. nil if it is not in ch,
   which the vocabulary forbids (T-web-theme-007) but a hand-edited theme could
   still produce."
  [v]
  (when-let [m (re-matches #"([0-9.]+)ch" (str v))]
    (js/parseFloat (nth m 1))))

(defn token->css
  "One token as the CSS custom properties it becomes: `[[prop value] …]`.

   A SCALAR token is one property — `:measure-prose \"66ch\"` → `--measure-prose`.

   A COMPOSITE token is several. `:type-h1` carries size, weight, line-height and
   tracking together, because a heading is ONE decision and four separate tokens
   would let its parts drift apart; DTCG's `typography` type says the same. CSS has
   no composite custom property, so it flattens: `--type-h1-size`,
   `--type-h1-weight`, `--type-h1-line-height`, and so on. The name is the token's
   name plus the field's, so the flattening is reversible by eye.

   A MEASURE IS TWO. `:measure-floor \"45ch\"` becomes `--measure-floor` AND
   `--measure-floor-ch: 45`, the same quantity as a bare NUMBER.

   That companion is not a convenience. The floor rule is
   `100cqi / (chars × ch-per-em)`, and CSS cannot divide by a length — it needs
   the character count as a number, and there is no way to take the unit off
   `45ch` in CSS. `--ch-per-em` is already published for exactly this reason
   (see init! — \"so a component can write the rule in pure CSS\"); this is its
   other half, and without it the rule had to be built in ClojureScript from the
   token NAME, which is how it came to strip the digits out of the string
   \"measure-floor\" and emit `calc(100cqi / ( * var(--ch-per-em)))` — invalid CSS
   that every browser drops. Found 2026-08-18, live since the floor was written.

   Pure, and public, because this is the part that can be wrong without showing:
   a mis-flattened composite yields a property nobody set and a heading that
   silently keeps the browser's default."
  [token value]
  (let [n (name token)]
    (cond
      (map? value)
      (for [[field v] value]
        [(str "--" n "-" (name field)) (str v)])

      (floor-chars value)
      [[(str "--" n) (str value)]
       [(str "--" n "-ch") (str (floor-chars value))]]

      :else
      [[(str "--" n) (str value)]])))

;; ---------------------------------------------------------------------------
;; The floor is a promise, not a refusal — George, 2026-08-15: "the minimum
;; legible size should be in chars and at some point when we cannot have less
;; chars will make the font smaller."
;; ---------------------------------------------------------------------------

(defn fluid-font-size
  "A CSS font-size that never lets its text set fewer than `floor-ch` characters.

   THE MEASURE IS THE INVARIANT AND THE TYPE IS THE DEPENDENT VARIABLE. That is
   the inversion this project had backwards all along: the type size was fixed and
   the line length was whatever the container happened to give, so a narrow island
   set eight words a line. Here the character count is held and the type gives way.

   NOTHING REFUSES TO DRAW. An earlier draft had a component decline below its
   floor; on an infinite canvas that is simply wrong — you zoom out and things get
   small, and a pane that blanked itself would be a hole in the canvas.

   `100cqi` is the container's own inline size, so this responds to the ISLAND the
   reader dragged rather than to the window — which is the only unit that means
   anything here. `min()` keeps the declared size as the ceiling, so nothing grows:
   a wide island reads at the size the theme asked for, and only a narrow one
   shrinks.

   `ch-per-em` is a FONT METRIC and must be measured, never assumed — it is 0.5561
   for the UI stack and 0.6 for monospace (measured 2026-08-15), and constant
   across sizes, which is what makes one number enough. It is measured at boot and
   published as `--ch-per-em`, so this reads it rather than taking it as an
   argument. The fallback is the measured UI-stack value, so a page whose script
   has not run yet gets the right floor instead of no font-size at all.

   TOKEN NAMES IN, CSS OUT, and no arithmetic here at all — 2026-08-18. This took
   `[declared-size floor-ch ch-per-em]` and multiplied two of them in
   ClojureScript, which meant a caller had to HAVE the numbers. widgets/cell-style
   did not: a design cell says `:floor measure-floor`, a token NAME. So it built
   the expression itself out of that name, stripped the non-digits, got an empty
   string, and rendered `calc(100cqi / ( * var(--ch-per-em)))`. One rule with two
   implementations, and the tested one was the unused one. Now there is one, it
   takes what a declaration actually carries, and the numbers stay in CSS where a
   theme switch can still move them without a re-render."
  [size-token floor-token]
  (str "min(var(--" (name size-token) "-size)"
       ", calc(100cqi / (var(--" (name floor-token) "-ch)"
       " * var(--ch-per-em, 0.5561))))"))

(defn- measure-ch-per-em!
  "How wide the digit zero is, as a fraction of the font size, for the document's
   own font. Measured at 100px for precision and torn down immediately.

   The DOM half of the rule above, kept to four lines for the same reason
   ruler.cljs keeps its measurement apart from its arithmetic: this cannot be
   tested in node, and everything around it can."
  []
  (let [el (.createElement js/document "span")]
    (set! (.. el -style -cssText) "position:absolute;visibility:hidden;font-size:100px;width:10ch")
    (.appendChild (.-body js/document) el)
    (let [w (.-width (.getBoundingClientRect el))]
      (.remove el)
      (when (pos? w) (/ w 1000)))))

(defn- store! [k]
  (try (.setItem js/localStorage storage-key (name k)) (catch :default _ nil)))

(defn- stored []
  (try (some-> (.getItem js/localStorage storage-key) keyword) (catch :default _ nil)))

(defn- store-overrides! [m]
  (try (.setItem js/localStorage overrides-key (pr-str m)) (catch :default _ nil)))

(defn- load-overrides []
  (try (some-> (.getItem js/localStorage overrides-key) reader/read-string)
       (catch :default _ nil)))

(defn apply-theme!
  "Write theme `k`'s tokens (base data ⊕ editor overrides) as --token CSS custom
   properties on the document root, update `active`, and persist the choice.
   Since 2026-08-15 that is every INTRINSIC token and not only the colours — a
   composite type token becomes several properties, see `token->css`.
   Unknown `k` falls back to the default so a stale localStorage value can never
   leave the UI unstyled. Also updates the Mantine color scheme so Mantine
   components match the IKC theme."
  [k]
  (let [k      (if (contains? themes k) k default)
        tokens (effective-tokens k)
        style  (.-style (.-documentElement js/document))]
    (doseq [[token value] tokens
            [prop v]      (token->css token value)]
      (.setProperty style prop v))
    (reset! active k)
    (reset! mantine-scheme (theme-scheme tokens))
    (store! k)))

(defn init!
  "Apply the persisted theme (or the default) at boot, restoring any saved token
   edits first so they re-merge on top. Safe to call before the app mounts;
   index.html's :root already carries the Amber defaults, so this only matters
   when a non-default theme or edits were chosen previously."
  []
  (reset! overrides (or (load-overrides) {}))
  (reset! mantine-scheme (theme-scheme (effective-tokens (or (stored) default))))
  (apply-theme! (or (stored) default))
  ;; The font metric the floor rule needs, published as a token like any other so
  ;; a component can write the rule in pure CSS. Measured rather than assumed
  ;; because it differs per font — 0.5561 for the UI stack, 0.6 for monospace.
  (when-let [r (measure-ch-per-em!)]
    (.setProperty (.-style (.-documentElement js/document)) "--ch-per-em" (str r))))

(defn theme-name [k] (get-in themes [k :name] (name k)))

;; ── Theme editor (ADR-graph-ui-v0-009 §4: a UI over the token data) ──────────────────────

(defonce ^:private editor-open (r/atom false))
(defn open-editor! [] (reset! editor-open true))
(defn close-editor! [] (reset! editor-open false))
(defn toggle-editor! [] (swap! editor-open not))

(defn- hex-colour?
  "Is `v` a `#rrggbb` literal? The test that decides whether a declared token can
   be edited by an `<input type=\"color\">` — which accepts nothing else, and
   silently rewrites whatever it is given to #000000 on the first click."
  [v]
  (boolean (re-matches #"#[0-9a-fA-F]{6}" (str v))))

(def ^:private group-order
  "The order the colour groups are shown in — surfaces first, because that is
   what a reader looks for. A declared group missing from this list is not
   dropped; it is shown after these, so a new group appears without an edit."
  [:surfaces :text :accent :semantic :highlight])

(def ^:private group-title
  "A display title per declared group. A group with no entry is titled from its
   own key, for the same reason group-order tolerates a stranger."
  {:surfaces "Surfaces" :text "Text" :accent "Accent"
   :semantic "Semantic" :highlight "Highlights"})

(def token-groups
  "PUBLIC, because the thing that was wrong here was invisible: :highlight was
   declared, themed in five themes, and missing from this editor, and no test
   could say so while this was private. T-web-theme-010 now can.

   The editable groups, PROJECTED FROM THE DECLARED VOCABULARY rather than typed
   out here.

   IT WAS A HAND-WRITTEN LIST OF FOUR until 2026-08-18, and the list was the
   defect. :highlight was declared, themed in all five themes, and absent from
   this editor — its own docstring said so and nothing made it false. Then the
   same thing nearly happened to :body: a token added to the declaration would
   have been invisible here until somebody remembered this vector.

   WHAT MAKES A GROUP EDITABLE is now a property of its VALUES, not a name in a
   list: every token in it is a `#rrggbb` literal in the default theme. That is
   exactly the condition `token-row` needs, so the two can no longer disagree.
   The measure/type/space/radius/stroke/elevation/layer groups fail it and are
   excluded by the same test rather than by being left out — and :focus-color,
   which is `var(--accent)` and not a hex literal, is excluded even though its
   group is otherwise colour-like.

   Editing the non-colour tokens still needs a different row per group, and is
   still not faked here."
  (let [v      (:token-vocabulary gen/theme-data)
        eff    (effective-tokens default)
        colour? (fn [tokens] (and (seq tokens) (every? #(hex-colour? (get eff %)) tokens)))
        ordered (concat group-order (remove (set group-order) (keys v)))]
    (for [g ordered
          :let [tokens (get v g)]
          :when (colour? tokens)]
      [(get group-title g (name g)) tokens])))

(defn- set-token!
  "Edit token `tk` of the active theme: write the CSS var live AND record it as an
   override on the active theme, so the edit is reactive (rows refresh) and is
   retained when the user switches themes and comes back."
  [tk value]
  (.setProperty (.-style (.-documentElement js/document)) (str "--" (name tk)) value)
  (let [m (swap! overrides assoc-in [@active tk] value)]
    (store-overrides! m)))

(def ^:private token-means
  "{token \"one sentence\"} — WHAT each token paints, as the vocabulary declares
   it (dsl/instances/themes.edn :token-means).

   INTENT, WHICH NO SCAN CAN RECOVER. `consumers` below says WHERE a token
   lands and is derived from the source; this says what it is FOR. validate-dsl
   holds the two lists together, so a token cannot gain a value and lose its
   sentence."
  (:token-means gen/theme-data))

(def ^:private consumers
  "{token [file …]} — where each token lands, generated from the source itself
   (dev/generate_themes.clj). It cannot go stale: a token used somewhere new
   says so on the next generate."
  (:token-consumers gen/theme-data))

(defn- short-name
  "src/cljs/graph_ui/web/widgets.cljs → widgets.cljs. The directory is the same
   for most of them, and the file is what a person recognises."
  [path]
  (last (str/split (str path) #"/")))

(defonce ^:private open-token (r/atom nil))

(defn- token-row
  "One token: its swatch, its name, its value — and WHAT IT PAINTS.

   THE LAST PART IS WHY THIS ROW CHANGED (2026-09-11). George: the editor
   \"shows colors that require a lot of experiments to find out where are
   applied\". A row said what a token is CALLED and never what it does, so the
   only way to learn was to change it and hunt. Now it carries the one-sentence
   :means the vocabulary declares, and opens to the files that read it."
  [tk v]
  (let [files (get consumers tk)
        means (get token-means tk)
        open? (= @open-token tk)]
    [:div.py-1
     [:div.flex.items-center.gap-2
      [:input {:type "color" :value v
               :class "w-8 h-6 rounded border cursor-pointer bg-transparent p-0"
               :style {:border-color "var(--border)"}
               :on-change #(set-token! tk (-> % .-target .-value))}]
      [:div.flex-1.min-w-0
       [:div.text-sm.truncate {:class "text-[var(--muted)]"} (str "--" (name tk))]
       (when means
         [:div.text-xs.leading-snug {:class "text-[var(--faint)]"} means])]
      [:span.text-xs.font-mono.shrink-0 {:class "text-[var(--faint)]"} v]
      ;; THE COUNT IS THE AFFORDANCE. A number says there is something to open
      ;; and how much of it, which a bare chevron does not.
      [:button.text-xs.shrink-0.px-1.rounded
       {:class (if open? "text-[var(--accent)]" "text-[var(--faint)]")
        :title "which files read this token"
        :on-click #(reset! open-token (when-not open? tk))}
       (if (seq files) (str (count files) " ▾") "—")]]
     (when open?
       [:div.text-xs.font-mono.pl-10.pb-1 {:class "text-[var(--faint)]"}
        (if (seq files)
          (str/join ", " (map short-name files))
          "nothing in the product reads this token yet")])]))

(defn editor
  "The theme editor drawer. Picks a theme and edits any token live — every
   change recolours the whole UI (the tokens ARE the palette). Saving a new
   theme (persisted as data) lands when themes become bus-backed frames; for
   now this is live editing + built-in selection."
  []
  (when @editor-open
    (let [act    @active
          _      @overrides                           ; re-render rows on live edits
          eff    (effective-tokens act)]
      [:<>
       ;; THE SCRIM AND THE PANEL ARE TWO LAYERS, and until 2026-08-16 they were
       ;; two bare Tailwind numbers, 40 and 50, sitting beside a :layer token
       ;; group that named exactly those two heights and that nothing read. The
       ;; values are unchanged; what changes is that they are now sayable.
       [:div.fixed.inset-0 {:style {:background "rgba(0,0,0,.35)"
                                    :zIndex     "var(--layer-overlay)"}
                            :on-click close-editor!}]
       ;; A PHONE GETS THE WHOLE WIDTH (SPEC-alatus-v0-002-02 row C-07): below
       ;; --viewport-narrow an 80-column drawer still leaves a sliver of page
       ;; under the scrim that answers nothing. The declared breakpoint asks
       ;; the question, as every other narrow rule does — not a pixel here.
       [:div.fixed.top-0.right-0.h-screen.flex.flex-col
        {:class (if (responsive/narrow?) "w-full border-l" "w-80 border-l")
         :style {:background   "var(--panel)"
                 :border-color "var(--border)"
                 :zIndex       "var(--layer-modal)"
                 :box-shadow   "var(--elevation-overlay)"}}
        [:div.flex.items-center.gap-2.px-4.py-3.border-b
         {:style {:border-color "var(--border)"}}
         [:span.font-bold.flex-1 {:class "text-[var(--heading)]"} "Theme editor"]
         [:span.cursor-pointer.text-lg {:class "text-[var(--muted)] hover:text-[var(--text)]"
                                        :on-click close-editor!} "✕"]]
        [:div.overflow-y-auto.px-4.py-3.flex-1
         [:div.flex.flex-wrap.gap-1.5.mb-3
          (for [k (theme-keys)]
            ^{:key (name k)}
            [:button {:on-click #(apply-theme! k)
                      :class (str "flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs "
                                  (if (= k @active) "text-[var(--accent)]" "text-[var(--text)]"))
                      :style {:background "var(--raised)"
                              :border-color (if (= k @active) "var(--accent)" "var(--border)")}}
             (let [kt (effective-tokens k)]
               [:span {:class "w-3 h-3 rounded-full"
                       :style {:background (:ground kt)
                               :border (str "1px solid " (:accent kt))}}])
             (theme-name k)])]
         [:p.text-xs.mb-3 {:class "text-[var(--faint)]"}
          "Edit any swatch to recolour the UI live. A theme is just this token set."]
         (for [[group tokens] token-groups]
           ^{:key group}
           [:div.mb-3
            [:div.text-xs.font-semibold.uppercase.tracking-wide.mb-1 {:class "text-[var(--faint)]"} group]
            (for [tk tokens] ^{:key (name tk)} [token-row tk (get eff tk)])])]]])))

(defn- swatch-style
  "A swatch that reads as the theme it invokes: its GROUND colour (the dominant
   surface a user identifies the theme by), ringed by its ACCENT so the dark
   themes stay distinguishable from each other. The active theme gets an extra
   outer ring in its own heading colour."
  [k active?]
  (let [t (effective-tokens k)]
    (cond-> {:background (:ground t)
             :border     (str "2px solid " (:accent t))}
      active? (assoc :box-shadow (str "0 0 0 2px " (:heading t))))))

(defn theme-selector
  "A compact row of theme swatches — click to switch. Minimal on purpose; the
   full theme editor (edit/create/save) arrives with the ikc1 chrome."
  []
  ;; `ikc-theme-swatches` names this row for ikc-base.css. Under a finger each
  ;; swatch is a 44 px target, and the padding of the row then made the top bar
  ;; 55 px high, not one row of 44 px (fault report 189f8375).
  (into [:div.flex.items-center.gap-1.5.px-1.py-1.rounded-md
         {:class "ikc-theme-swatches ikc-bg-raised border ikc-bd-border"
          :title "Theme"}]
        (for [k (theme-keys)]
          ^{:key (name k)}
          [:button
           {:on-click #(apply-theme! k)
            :title (theme-name k)
            :class (str "w-4 h-4 rounded-full transition " (when (= k @active) "scale-110"))
            :style (swatch-style k (= k @active))}])))
